import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Download, FileSpreadsheet, Loader2 } from 'lucide-react';

/**
 * ExportModal - Reusable export modal component for admin pages
 * 
 * @param {boolean} open - Modal open state
 * @param {function} onOpenChange - Modal state change handler
 * @param {string} title - Modal title (e.g., "Export Written Questions")
 * @param {string} description - Modal description
 * @param {Array} filters - Array of filter configs: { key, label, options: [{value, label}], value, onChange }
 * @param {Array} columns - Array of column configs: { key, label, checked, onChange }
 * @param {function} onExport - Export handler function(format, selectedFilters, selectedColumns)
 * @param {boolean} exporting - Loading state
 * @param {number} totalCount - Total items that will be exported
 */
const ExportModal = ({
  open,
  onOpenChange,
  title = 'Export Data',
  description = 'Choose filters and columns to export',
  filters = [],
  columns = [],
  onExport,
  exporting = false,
  totalCount = 0
}) => {
  const [format, setFormat] = useState('csv');
  const [selectedColumns, setSelectedColumns] = useState(
    columns.reduce((acc, col) => ({ ...acc, [col.key]: col.checked !== false }), {})
  );

  const handleColumnToggle = (key) => {
    setSelectedColumns(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSelectAll = () => {
    const allSelected = columns.every(col => selectedColumns[col.key]);
    const newState = columns.reduce((acc, col) => ({ ...acc, [col.key]: !allSelected }), {});
    setSelectedColumns(newState);
  };

  const handleExport = () => {
    const activeColumns = Object.entries(selectedColumns)
      .filter(([_, checked]) => checked)
      .map(([key]) => key);
    
    const activeFilters = filters.reduce((acc, filter) => {
      if (filter.value && filter.value !== 'all') {
        acc[filter.key] = filter.value;
      }
      return acc;
    }, {});

    onExport(format, activeFilters, activeColumns);
  };

  const selectedCount = Object.values(selectedColumns).filter(Boolean).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-green-600" />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Export Format */}
          <div className="space-y-2">
            <Label>Export Format</Label>
            <Select value={format} onValueChange={setFormat}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="csv">CSV (.csv)</SelectItem>
                <SelectItem value="excel">Excel (.xlsx)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Filters */}
          {filters.length > 0 && (
            <div className="space-y-3">
              <Label className="text-base font-semibold">Filters (Export matching data)</Label>
              <div className="grid grid-cols-2 gap-3">
                {filters.map((filter) => (
                  <div key={filter.key} className="space-y-1">
                    <Label className="text-sm text-gray-600">{filter.label}</Label>
                    <Select value={filter.value || 'all'} onValueChange={filter.onChange}>
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        {filter.options?.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Column Selection */}
          {columns.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Columns to Export</Label>
                <Button variant="ghost" size="sm" onClick={handleSelectAll}>
                  {columns.every(col => selectedColumns[col.key]) ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2 p-3 bg-gray-50 rounded-lg max-h-48 overflow-y-auto">
                {columns.map((col) => (
                  <div key={col.key} className="flex items-center gap-2">
                    <Checkbox
                      id={`col-${col.key}`}
                      checked={selectedColumns[col.key]}
                      onCheckedChange={() => handleColumnToggle(col.key)}
                    />
                    <Label htmlFor={`col-${col.key}`} className="text-sm cursor-pointer">
                      {col.label}
                    </Label>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500">
                {selectedCount} of {columns.length} columns selected
              </p>
            </div>
          )}

          {/* Export Summary */}
          {totalCount > 0 && (
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>{totalCount.toLocaleString()}</strong> records will be exported based on current filters
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={exporting}>
            Cancel
          </Button>
          <Button 
            onClick={handleExport} 
            disabled={exporting || selectedCount === 0}
            className="bg-green-600 hover:bg-green-700"
          >
            {exporting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Export {format.toUpperCase()}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExportModal;
